/**
 * THE PRICE LADDER, AS ROWS (GSD revamp, 2026-10-15).
 *
 * Every figure here is derived from src/config/workUnits.ts, the single source of prices (its backend twin and
 * Stripe's TIER_CONFIG mirror it). Nothing is typed twice: `/`, `/pricing`, `/subscription`, the ROI calculator,
 * llms.txt and the JSON-LD offers all read these helpers, so the same inputs give the same numbers everywhere
 * (acceptance criterion 2). Words live in each page's content module, never here.
 */
import {
  MONTHLY_WU_PER_PRESTON,
  PREPAY_UP_TO_PCT,
  STANDARD_RATE_USD_PER_WU,
  TYPICAL_MONTH,
  WORK_RATE_CARD,
  WU_PRICING,
  annualSeatRung,
  formatPriceUsd,
  hireQuote,
  monthlySeatRung,
  prepayOffAnnualPct,
  singleProductCeiling,
  type AnnualRung,
  type HireCadence,
  type MonthlyRung,
} from './workUnits';
import type { LadderTier } from './planTiers';

export type LadderRungKey = 'standard' | MonthlyRung['key'] | AnnualRung['key'];

export interface LadderRow {
  key: LadderRungKey;
  /** What the row sells: the standard rate (a rate, not a plan), a monthly rung (no commitment) or an annual rung. */
  cadence: 'standard' | 'monthly' | 'annual';
  /** Per Prest0n employee per month. */
  monthlyUsd: number;
  /** $ per PRO Work Unit (pr0), as published (rounded to the cent). */
  usdPerWu: number;
  /** % saved against the standard rate, as published; null on the standard rate. */
  savePct: number | null;
  /** The team sizes the rung sells, inclusive (the standard rate takes any size the ladder sells). */
  minSeats: number;
  maxSeats: number;
  /** The tier a hire on this rung buys; null on the standard rate, which is a rate, not a plan. */
  tier: LadderTier | null;
}

/** A rung of WU_PRICING as a row. */
const rowOf = (cadence: 'monthly' | 'annual') => (r: MonthlyRung | AnnualRung): LadderRow => ({
  key: r.key,
  cadence,
  monthlyUsd: r.monthlyUsd,
  usdPerWu: r.usdPerWu,
  savePct: r.savePct,
  minSeats: r.minSeats,
  maxSeats: r.maxSeats,
  tier: r.tier,
});

/** The monthly rungs alone, smallest team first (monthly is priced by team size since edition 2026-12). */
export const MONTHLY_LADDER: readonly LadderRow[] = WU_PRICING.monthlyRungs.map(rowOf('monthly'));

/** The annual rungs alone, smallest team first. */
export const ANNUAL_LADDER: readonly LadderRow[] = WU_PRICING.annualRungs.map(rowOf('annual'));

/** The published ladder, standard rate first, then every monthly rung, then every annual rung, in the pricing page's order. */
export const PRICE_LADDER: readonly LadderRow[] = [
  { key: 'standard', cadence: 'standard', monthlyUsd: WU_PRICING.standard.monthlyUsd, usdPerWu: WU_PRICING.standard.usdPerWu, savePct: null, minSeats: 1, maxSeats: WU_PRICING.selfServeMaxSeats, tier: null },
  ...MONTHLY_LADDER,
  ...ANNUAL_LADDER,
];

/** The lowest published $ per pr0 ("from $1.00 per work unit"): the largest annual rung. */
export const LOWEST_USD_PER_WU: number = Math.min(...PRICE_LADDER.map((r) => r.usdPerWu));

/** From this many Prest0n employees, larger organizations book a meeting (no price past the last rung is published). */
export const BY_MEETING_FROM: number = WU_PRICING.selfServeMaxSeats + 1;

/**
 * The primary offer (rulings R2, R9): three Prest0n employees on an annual commitment billed monthly, the count every
 * picker starts at and every headline prices. It carries the old one-Prest0n discount for a lower commitment. The price
 * headline's words (./priceHeadlineWords.ts, which Prest0n's facts share byte for byte) carry the same count for the
 * server, held equal to this one by tests/landing/priceHeadline.test.tsx.
 */
export const PRIMARY_OFFER_SEATS = 3;

/** The ladder row for a cadence and team size (a prepaid year is not a rung of its own: it is priced on the annual rung's sizes). */
export function ladderRowFor(cadence: Exclude<HireCadence, 'prepay'> | 'standard', seats = 1): LadderRow {
  if (cadence === 'standard') return PRICE_LADDER[0];
  if (cadence === 'monthly') {
    const rung = monthlySeatRung(seats);
    return MONTHLY_LADDER.find((r) => r.key === rung.key) ?? MONTHLY_LADDER[MONTHLY_LADDER.length - 1];
  }
  const rung = annualSeatRung(seats);
  return ANNUAL_LADDER.find((r) => r.key === rung.key) ?? ANNUAL_LADDER[ANNUAL_LADDER.length - 1];
}

export interface PrepayRow {
  seats: number;
  /** The annual rung the prepaid year is priced against (the same team sizes). */
  rung: LadderRow;
  /** What prepaying takes off that annual rung, in whole percent: the selection's own figure (up to PREPAY_UP_TO_PCT). */
  discountPct: number;
  /** Monthly equivalent per Prest0n employee: the prepaid rung's own price ($600 for one). */
  perSeatMonthlyUsd: number;
  /** The one payment for the year, for the whole team ($7,200 for one). */
  upfrontTotalUsd: number;
}

/** Prepay the year: the prepaid rung's own price for the team size (up to PREPAY_UP_TO_PCT off its annual rung), paid once. Never bonus units. */
export function prepayRow(seats = 1): PrepayRow {
  const q = hireQuote('prepay', seats);
  return {
    seats: q.seats,
    rung: ladderRowFor('annual', q.seats),
    discountPct: prepayOffAnnualPct(q.seats),
    perSeatMonthlyUsd: q.perSeatMonthlyUsd,
    upfrontTotalUsd: q.upfrontTotalUsd as number,
  };
}

/** Every prepaid rung, smallest team first: one row per annual rung's sizes. */
export const PREPAY_LADDER: readonly PrepayRow[] = WU_PRICING.annualRungs.map((r) => prepayRow(r.minSeats));

/** The most a prepaid year takes off its annual rung ("up to 20% off"), from the rungs. */
export { PREPAY_UP_TO_PCT };

/** Round to the cent, the way a price is shown. */
export const toCents = (usd: number): number => Math.round(usd * 100) / 100;

export interface RateCardRow {
  task: string;
  wu: number;
  unit: string;
  receive: string;
  /** Dollars for one of it at the rung's own rate, to the cent (memo: 8 × $700 ÷ 450 = $12.44 on the three-employee annual rung). */
  usd: number;
}

/**
 * The rate card priced at a rung's per-employee monthly price: each task's pr0 at that price over the unit — the exact
 * rate the meter charges a plan's allocation at (a lot's $ per pr0 is the price over its pr0, billing/planAllocation.ts),
 * not the rounded $ per pr0 shown beside it — to the cent, so a task's dollars here are what it is charged.
 */
export function rateCardForPrice(perSeatMonthlyUsd: number): RateCardRow[] {
  return WORK_RATE_CARD.map((r) => ({ task: r.task, wu: r.wu, unit: r.unit, receive: r.receive, usd: toCents((r.wu * perSeatMonthlyUsd) / MONTHLY_WU_PER_PRESTON) }));
}

export interface CapacityRow {
  key: string;
  item: string;
  count: number;
  wuEach: number;
  /** All of one kind: the whole month's allocation spent on this item alone. */
  allOfOneKind: number;
}

/** What one Prest0n employee's monthly allocation buys: the typical month, and each item's ceiling alone. */
export const CAPACITY: readonly CapacityRow[] = TYPICAL_MONTH.map((r) => ({
  key: r.key,
  item: r.item,
  count: r.count,
  wuEach: r.wuEach,
  allOfOneKind: singleProductCeiling(r.wuEach),
}));

export { MONTHLY_WU_PER_PRESTON, STANDARD_RATE_USD_PER_WU, formatPriceUsd };
